import { useState, useRef } from 'react';
import {
  AlertDialog,
  JFormik,
  selectUser,
  AlertDialogProps,
} from '@centerpiece/components';
import {
  Box,
  Button,
  Typography,
  FormControlLabel,
  FormGroup,
  Switch,
  Tooltip,
  CircularProgress,
  Grid,
  Theme,
} from '@mui/material';
import { ArrowBack, Save, Edit, Unsubscribe } from '@mui/icons-material';
import * as Yup from 'yup';
import moment from 'moment';
import {
  Action,
  Coordination,
  Attachment,
  Contact,
  Message,
  Response,
  Event,
  ProjectContact,
  Reaction,
} from 'store/api/models';
import { useGetContactsQuery } from 'store/api/contact.api';
import {
  useCreateMessageMutation,
  useGetProjectContactsQuery,
  useGetTagsQuery,
  useLazyGetAttachmentQuery,
  usePatchMessageMutation,
} from 'store/api';
import { useProjectContext } from 'contexts/project.context';
import {
  CreateMessageStatusProps,
  CreateMessageStatus,
} from 'statuses/CreateMessageStatus';
import { downloadFile } from 'utils/downloadFile.util';
import { useMessageForm } from 'jformik/useMessageForm';
import { useEventForm } from 'jformik/useEventForm';
import { useSelector } from 'react-redux';
import { ContactComponent } from 'components/ContactComponent';
import { useAllContacts } from 'utils/contacts.utils';
import { useItemContext } from 'contexts/item.context';
import {
  messageInitialValues,
  responseInitialValues,
} from 'utils/messaging.utils';

export interface EventTabProps {
  onClose?: Function;
  onSubmit?: (values: any) => void | Promise<any>;
  back?: boolean;
  message?: Message;
  response?: Response;
}

export const EventTab = ({
  onClose = () => {},
  onSubmit = values => {},
  back = false,
  message,
  response,
}: EventTabProps) => {
  const {
    id,
    project,
    project_id,
    item,
    availabilities,
    title,
    itemLoading,
    coordination,
    messages,
    task,
    event,
    refetchItem,
    setActiveTab,
  } = useItemContext();
  const replied =
    messages?.find(m => m.message.id === message?.id)?.responses?.length ||
    0 > 0;
  const user = useSelector(selectUser);
  const allContacts = useAllContacts();
  const formRef = useRef<any>(null);
  const [alertDetails, setAlertDetails] = useState<AlertDialogProps>();
  const [editing, setEditing] = useState<boolean | undefined>(
    message === undefined ? undefined : false
  );
  const [createMessage] = useCreateMessageMutation();
  const [patchMessage, patchMessageResults] = usePatchMessageMutation();
  const [getAttachment] = useLazyGetAttachmentQuery({ refetchOnFocus: false });
  const [messageStatus, setMessageStatus] = useState<
    Omit<CreateMessageStatusProps, 'close'>
  >({
    open: false,
    loading: false,
    creatingMessage: false,
    creatingMessageError: false,
    attachments: { pending: 0, created: 0, failed: 0 },
  });
  const [preview, setPreview] = useState<boolean>(false);
  const [openContact, setOpenContact] = useState<{
    open: boolean;
    id?: number;
    disabled?: boolean;
    callback?: Function;
    close?: Function;
    initialValues?: any;
  }>({ open: false });

  const { events } = useProjectContext();
  const { data: tags } = useGetTagsQuery({});
  const eventForm: any[] = useEventForm({
    events: events.filter(e => !!e.start || !!e.end),
    tags: tags,
    preview: preview,
    disabled: editing === false, // !!message || !!response,
    editing: editing,
    replied: !!response,
    notice: true,
    project: project,
    title: title,
    setOpenContact: setOpenContact,
    contacts: allContacts,
    onDownload: async (file: any) => {
      const attachment: Attachment = await getAttachment(file.id).unwrap();
      await downloadFile(attachment);
    },
    // disable_type: !!coordination?.type,
  });

  const handleToggle = () => {
    if (formRef.current) {
      // const { values, setFieldValue } = formRef.current;
      // const cleanvalues = values?.form?.body?.map((item: any) => {
      //   if (item?.action) {
      //     const cleanItem = {
      //       ...item,
      //       action: {
      //         ...item.action,
      //         reactiondetails: undefined,
      //         reactionattachments: undefined,
      //         status: undefined,
      //       },
      //     };
      //     return {
      //       ...cleanItem,
      //       form: {
      //         ...cleanItem,
      //       },
      //     };
      //   } else if (item?.preamble) {
      //     return { ...item, preamble: undefined, replyattachments: undefined };
      //   } else {
      //     return item;
      //   }
      // });
      // setFieldValue('form.body', cleanvalues);
      setPreview(prev => !prev);
    }
  };
  return (
    <Box>
      <AlertDialog
        open={!!alertDetails?.open}
        title={alertDetails?.title || ''}
        content={alertDetails?.content || ''}
        onClose={() => {
          alertDetails?.onClose && alertDetails.onClose();
          setAlertDetails(undefined);
        }}
        onConfirm={() => {
          alertDetails?.onConfirm && alertDetails.onConfirm();
          setAlertDetails(undefined);
        }}
      />
      {openContact?.open && (
        <ContactComponent
          enableOrganization={true}
          {...openContact}
          close={() => {
            openContact.close && openContact.close();
            setOpenContact({ open: false });
          }}
        />
      )}
      <Box
        sx={{
          display: openContact?.open ? 'none' : 'block',
          position: 'relative',
        }}
      >
        <CreateMessageStatus
          close={() =>
            setMessageStatus({
              open: false,
              loading: false,
              creatingMessage: false,
              creatingMessageError: false,
              attachments: { pending: 0, created: 0, failed: 0 },
            })
          }
          {...messageStatus}
        />
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap={'nowrap'}
          spacing={0}
          sx={{ width: '100%' }}
        >
          <Grid item>
            {back && (
              <Button
                startIcon={<ArrowBack />}
                color="secondary"
                onClick={() => onClose()}
              >
                <Typography
                  sx={{
                    lineHeight: 1,
                    marginTop: 0.25,
                    fontSize: 15,
                  }}
                >
                  Back
                </Typography>
              </Button>
            )}
          </Grid>

          <Grid item>
            <Grid
              container
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              flexWrap={'nowrap'}
              spacing={0}
            >
              {!response && !message && !back && (
                <Grid item sx={{ pb: 1 }}>
                  <Tooltip
                    title={
                      'Toggle on to see what your recipients will see. Any edits made in this mode will be discarded when the message is sent'
                    }
                  >
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            color="secondary"
                            checked={preview}
                            onChange={() => handleToggle()}
                          />
                        }
                        label={
                          <Typography variant="button">
                            EMAIL PREVIEW
                          </Typography>
                        }
                      />
                    </FormGroup>
                  </Tooltip>
                </Grid>
              )}
              {editing && !replied && (
                <Grid item>
                  <Button
                    startIcon={
                      patchMessageResults.isLoading ? (
                        <CircularProgress sx={{ fontSize: '0.5rem' }} />
                      ) : (
                        <Unsubscribe fontSize="small" />
                      )
                    }
                    color="warning"
                    // onClick={() => setEditing(prev => !prev)}
                    onClick={async () => {
                      if (editing) {
                        setAlertDetails({
                          open: true,
                          PaperProps: {
                            sx: {
                              border: (theme: Theme) =>
                                `10px solid ${theme.palette.warning.light}`,
                            },
                          },
                          title: `Are you sure you want to cancel this message?`,
                          content: `The email will be recalled from all recipients.`,
                          onConfirm: async () => {
                            setEditing(false);
                            if (formRef.current) {
                              const { submitForm, setFieldValue } =
                                formRef.current;
                              setFieldValue('recipients', []);
                              setFieldValue('cc', []);
                              setFieldValue('bcc', []);
                              setFieldValue('form.canceled', true);
                              submitForm();
                            }
                          },
                          onClose: () => setEditing(false),
                        });
                      } else {
                        setEditing(true);
                      }
                    }}
                  >
                    Cancel Message
                  </Button>
                </Grid>
              )}
              {message && !replied && (
                <Grid item>
                  <Button
                    startIcon={
                      patchMessageResults.isLoading ? (
                        <CircularProgress sx={{ fontSize: '0.5rem' }} />
                      ) : editing ? (
                        <Save fontSize="small" />
                      ) : (
                        <Edit fontSize="small" />
                      )
                    }
                    color="secondary"
                    // onClick={() => setEditing(prev => !prev)}
                    onClick={async () => {
                      if (editing) {
                        setAlertDetails({
                          open: true,
                          title: `Send update to participants`,
                          content: `Are you sure you want to update the meeting notice?`,
                          onConfirm: async () => {
                            setEditing(false);
                            if (formRef.current) {
                              formRef.current.submitForm();
                            }
                          },
                          onClose: () => setEditing(false),
                        });
                      } else {
                        setEditing(true);
                      }
                    }}
                  >
                    {editing ? 'Save' : 'Edit'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Box sx={{ paddingTop: back ? 1 : 0 }}>
          <Box>
            <JFormik
              key={JSON.stringify(formRef.current?.values?.recipients)}
              formRef={formRef}
              initialValues={
                formRef.current?.values
                  ? { ...formRef.current.values }
                  : !!response
                  ? responseInitialValues(
                      response,
                      'To',
                      response.r_message_r.contact_r
                    )
                  : message
                  ? messageInitialValues(message, 'TO', message.contact_r)
                  : {
                      from: [
                        {
                          ...user,
                          name: `${user.firstname} ${user.lastname}`,
                        },
                      ],
                      subject: `${project?.title}: ${title}`,
                      type: 'Availability',
                      form: {
                        canceled: false,
                        body: [
                          {
                            type: 'auto_salutation',
                            text: 'Hey {{first_name}},\n',
                            disabled: true,
                          },
                          {
                            text: 'We have an install coming up. The date range below is when the job-site will be ready. Let us know if you are available by selecting the dates that work for you to complete this install.  If you are not available during this date range, feel free to suggest new dates. ',
                          },
                          {
                            type: 'date_range',
                            text: 'Please also provide your quote. We have attached all the job files you need.',
                            action: {
                              title: title,
                              daterange: {
                                default: [
                                  {
                                    id: 1,
                                    start: null,
                                    end: null,
                                  },
                                ],
                                reply: [],
                                suggest: [],
                              },
                            },
                          },
                          {
                            type: 'cost_breakdown',
                            text: '',
                            action: {
                              custom_inputs: [
                                {
                                  custom_input: {
                                    label: 'Total Cost',
                                  },
                                  new: true,
                                },
                              ],
                            },
                          },
                          {
                            type: 'share_file',
                            text: '\nThank you.',
                          },
                        ],
                        copy_project: true,
                        auto_salutation: true,
                      },
                    }
              }
              disabled={editing === false} //!!message || messageStatus.open}
              validationSchema={Yup.object()
                .shape({
                  subject: Yup.string()
                    .nullable()
                    .required(`Please add a subject.`),
                  form: Yup.object().shape({
                    body: Yup.array()
                      .nullable()
                      .min(1, `Please add an email message.`)
                      .required('Please add an email message.'),
                  }),
                })
                .test(
                  'recipients',
                  'At least one field must have a value',
                  function (this: Yup.TestContext, value) {
                    const { path, createError } = this;
                    const { recipients = [], cc = [], bcc = [] } = value;
                    if (
                      !(
                        recipients?.length > 0 ||
                        cc?.length > 0 ||
                        bcc?.length > 0
                      )
                    ) {
                      const keys = ['recipients', 'cc', 'bcc'];
                      const errors = keys
                        .map(key => {
                          return new Yup.ValidationError(
                            'A valid email address is required in To, Cc, or Bcc',
                            value[key],
                            key
                          );
                        })
                        .filter(Boolean);
                      return createError({
                        message: () => errors,
                      });
                    }
                    return false;
                  }
                )}
              handleSubmit={async (values: any) => {
                const numFiles: number =
                  values?.form?.body?.reduce(
                    (total: number, item: any) =>
                      total +
                        item?.action?.attachments?.filter((a: any) => !a.id)
                          ?.length || 0,
                    0
                  ) || 0;
                setMessageStatus({
                  open: true,
                  loading: true,
                  creatingMessage: true,
                  creatingMessageError: false,
                  attachments: { pending: numFiles, created: 0, failed: 0 },
                  edit: !!values.id,
                });
                const msgvalues = {
                  ...values,
                  form: {
                    ...values.form,
                    body: values?.form?.body
                      ?.map((item: any, index: number) => {
                        if (item.type) {
                          return {
                            ...item,
                            form: {
                              ...item,
                              name: `form.body.${index}.action`,
                            },
                          };
                        } else {
                          return item;
                        }
                      })
                      ?.filter((i: any) => i),
                  },
                  actions: values?.form?.body
                    ?.map((item: any, index: number) => {
                      if (item.type) {
                        return {
                          ...item?.action,
                          form: {
                            ...item,
                            name: `form.body.${index}.action`,
                          },
                        };
                      }
                    })
                    ?.filter((i: any) => i),
                  item: id,
                  type: 'Availability',
                };
                try {
                  let newMessage: Message;
                  if (msgvalues.id > 0) {
                    newMessage = await patchMessage(msgvalues).unwrap();
                  } else {
                    newMessage = await createMessage(msgvalues).unwrap();
                  }
                  // const newMessage: Message = await createMessage(
                  //   msgvalues
                  // ).unwrap();
                  if (newMessage.id > 0) {
                    setMessageStatus(currentStatus => ({
                      ...currentStatus,
                      creatingMessage: false,
                    }));
                    if (newMessage?.actions?.length > 0) {
                      for (let j = 0; j < newMessage?.actions.length; j++) {
                        const action: Action = newMessage.actions[j];
                        const newAttachments = action?.attachments?.filter(
                          a => a.url
                        );
                        if (newAttachments?.length > 0) {
                          for (let i = 0; i < newAttachments.length; i++) {
                            const attachment: Attachment = newAttachments[i];
                            const file = msgvalues.actions[j].attachments?.find(
                              (a: File) => a.name === attachment.name
                            );
                            if (attachment.url && file.file) {
                              const res = await fetch(attachment.url, {
                                method: 'PUT',
                                body: file.file,
                              });

                              if (res.ok) {
                                setMessageStatus(currentStatus => ({
                                  ...currentStatus,
                                  attachments: {
                                    pending:
                                      currentStatus.attachments.pending - 1,
                                    created:
                                      currentStatus.attachments.created + 1,
                                    failed: currentStatus.attachments.failed,
                                  },
                                }));
                              } else {
                                setMessageStatus(currentStatus => ({
                                  ...currentStatus,
                                  attachments: {
                                    pending:
                                      currentStatus.attachments.pending - 1,
                                    created: currentStatus.attachments.created,
                                    failed:
                                      currentStatus.attachments.failed + 1,
                                  },
                                }));
                              }
                            } else {
                              setMessageStatus(currentStatus => ({
                                ...currentStatus,
                                attachments: {
                                  pending:
                                    currentStatus.attachments.pending - 1,
                                  created: currentStatus.attachments.created,
                                  failed: currentStatus.attachments.failed + 1,
                                },
                              }));
                            }
                          }
                        }
                      }
                    }
                    setMessageStatus(currentStatus => ({
                      ...currentStatus,
                      loading: false,
                    }));
                    onSubmit(values);
                    onClose();
                    refetchItem();
                  } else {
                    setMessageStatus(currentStatus => ({
                      ...currentStatus,
                      loading: false,
                      creatingMessage: false,
                      creatingMessageError: true,
                    }));
                  }
                } catch (error) {
                  setMessageStatus(currentStatus => ({
                    ...currentStatus,
                    loading: false,
                    creatingMessage: false,
                    creatingMessageError: true,
                  }));
                  console.error('rejected', error);
                }
              }}
              containerGridProps={{ spacing: 2 }}
              form={eventForm}
            />
          </Box>
          {message && message?.created_at && message?.updated_at && (
            <Typography variant="caption">
              <i>
                Message{' '}
                {moment(message.updated_at).isBefore(
                  moment(message.created_at).add(5, 'minutes')
                )
                  ? `sent`
                  : `updated`}{' '}
                {`${moment(message?.updated_at).format(
                  'dddd, MMMM Do YYYY, h:mm a'
                )}`}
              </i>
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
